/**
 * Permission Messages - Single source of truth for all permission-related messages
 * Used across Interview.tsx and AudioInterview.tsx for consistent messaging
 */

// Permission Error Types
export type PermissionType = 'camera' | 'microphone' | 'camera_and_microphone';

// Toast message types
export interface PermissionToastConfig {
  title: string;
  message: string;
}

// Permission request messages (shown on page load)
export const PERMISSION_REQUEST_MESSAGES: Record<PermissionType, PermissionToastConfig> = {
  camera: {
    title: 'Device Error',
    message: 'Please allow camera access in your browser settings to continue with the video screener.',
  },
  microphone: {
    title: 'Device Error',
    message: 'Please allow microphone access in your browser settings to continue with the screener.',
  },
  camera_and_microphone: {
    title: 'Device Error',
    message: 'Please allow camera and microphone access in your browser settings to continue with the video screener.',
  },
};

// Permission blocked messages (shown when user clicks Start Recording with blocked permissions)
export const PERMISSION_BLOCKED_MESSAGES: Record<PermissionType, PermissionToastConfig> = {
  camera: {
    title: 'Device Error',
    message: 'Camera access is blocked. Click the camera icon in the address bar (or site settings) to allow access, then refresh the page.',
  },
  microphone: {
    title: 'Device Error',
    message: 'Microphone access is blocked. Click the camera/microphone icon in the address bar (or site settings) to allow access, then refresh the page.',
  },
  camera_and_microphone: {
    title: 'Device Error',
    message: 'Camera and microphone access is blocked. Click the camera/microphone icon in the address bar (or site settings) to allow access, then refresh the page.',
  },
};

// Device not found messages
export const DEVICE_NOT_FOUND_MESSAGES: Record<PermissionType, PermissionToastConfig> = {
  camera: {
    title: 'Device Error',
    message: 'No camera found. Please connect a camera device and refresh the page.',
  },
  microphone: {
    title: 'Device Error',
    message: 'No microphone found. Please connect a microphone device and refresh the page.',
  },
  camera_and_microphone: {
    title: 'Device Error',
    message: 'No camera or microphone found. Please connect devices and refresh the page.',
  },
};

// Device disconnected messages (when selected device is no longer available)
export const DEVICE_DISCONNECTED_MESSAGES: Record<PermissionType, PermissionToastConfig> = {
  camera: {
    title: 'Device Error',
    message: 'Your selected camera is not available. Please reconnect your camera and try again.',
  },
  microphone: {
    title: 'Device Error',
    message: 'Your selected microphone is not available. Please reconnect your microphone and try again.',
  },
  camera_and_microphone: {
    title: 'Device Error',
    message: 'Your selected camera and microphone are not available. Please reconnect your devices and try again.',
  },
};

// Permission revoked during recording (device still exists but access was revoked)
export const PERMISSION_REVOKED_MESSAGES: Record<PermissionType, PermissionToastConfig> = {
  camera: {
    title: 'Device Error',
    message: 'Camera access was revoked during recording. Please allow camera access and try again.',
  },
  microphone: {
    title: 'Device Error',
    message: 'Microphone access was revoked during recording. Please allow microphone access and try again.',
  },
  camera_and_microphone: {
    title: 'Device Error',
    message: 'Camera and microphone access was revoked during recording. Please allow access and try again.',
  },
};

// Generic error messages
export const GENERIC_ERROR_MESSAGES = {
  connection_lost: {
    title: 'Connection Lost',
    message: 'You are offline. Please reconnect to continue.',
  },
  recording_error: {
    title: 'Recording Error',
    message: 'Recording failed. Please check your devices and try again.',
  },
  no_tries_remaining: {
    title: 'No Tries Remaining',
    message: 'No more recording attempts remaining.',
  },
  audio_silence: {
    title: 'Are you talking?',
    message: "We can't hear you. Please check your microphone and try again.",
  },
  general_device_error: {
    title: 'Device Error',
    message: 'Could not access your device. Please check your browser permissions and try again.',
  },
  device_constraint_error: {
    title: 'Recording Unavailable',
    message: 'Your camera or microphone could not be started. Please try a different browser, close other apps using your camera, or use a different device.',
  },
  // VI-431: shown whenever a recording attempt fails (connection drop, recorder
  // error, timeout, server error). Device-specific failures reuse this title with
  // a specific, actionable message instead.
  recording_failed: {
    title: 'Recording Failed',
    message: 'Recording failed. Please try again.',
  },
  microphone_unavailable_error: {
    title: 'Recording Unavailable',
    message: 'Your microphone could not be started. Please close other apps using your microphone, or try a different device or browser.',
  },
  submit_failed: {
    title: 'Submission Failed',
    message: 'We could not submit your answer. Please check your connection and try again.',
  },
};

/**
 * Helper function to get the appropriate error message based on error type
 */
export const getPermissionErrorMessage = (
  error: any,
  permissionType: PermissionType
): PermissionToastConfig => {
  if (error?.name === 'NotAllowedError') {
    return PERMISSION_BLOCKED_MESSAGES[permissionType];
  }
  if (error?.name === 'NotFoundError') {
    return DEVICE_NOT_FOUND_MESSAGES[permissionType];
  }
  if (error?.name === 'OverconstrainedError') {
    return DEVICE_DISCONNECTED_MESSAGES[permissionType];
  }
  return {
    title: GENERIC_ERROR_MESSAGES.general_device_error.title,
    message: GENERIC_ERROR_MESSAGES.general_device_error.message,
  };
};
